import { MAPBOX_TOKEN } from "@/lib/env";
import { tagRouteSource } from "@/lib/routeDiagnostics";
import { decodePolyline, totalDistance, type LngLat } from "@/utils/polyline";
import { detectEpicHighways } from "@/utils/epicHighways";

// Re-export so downstream modules that type their coords off the routing
// API don't need to know LngLat actually lives in utils/polyline.
export type { LngLat };

export type TravelMode = "driving" | "walking" | "biking";

export interface RouteStep {
  instruction: string;
  distance: number;
  duration: number;
  /** Raw Mapbox road name/ref. Used to identify named arterials like US-9
   *  even when a provider under-reports road-class metadata. */
  roadName?: string;
  roadRef?: string;
  highTrafficName?: string;
  /** Road classes for this step (from Mapbox `intersections[].classes`).
   *  Common values: "motorway", "toll", "tunnel", "restricted", "ferry". */
  classes?: string[];
  /** Lane guidance for the maneuver at the end of this step, when Mapbox
   *  supplied it. Each entry corresponds to one lane on the road at the
   *  point of maneuver; `valid` means "you may use this lane to complete
   *  the upcoming turn." Surfaced visually in the maneuver banner so the
   *  driver knows which lane to be in. */
  lanes?: Array<{ valid: boolean; indications: string[] }>;
  /** Maneuver type from Mapbox — used to detect complex maneuvers (off ramp,
   *  on ramp, fork, roundabout, rotary) for the detailed overlay UI. Common
   *  values: "depart", "turn", "continue", "merge", "on ramp", "off ramp",
   *  "fork", "end of road", "roundabout", "rotary", "exit roundabout",
   *  "exit rotary", "use lane", "notification", "arrive". */
  maneuverType?: string;
  /** Modifier ("left", "right", "slight left", "uturn", etc.). */
  maneuverModifier?: string;
  /** Roundabout exit number when the maneuver is a roundabout/rotary. */
  exit?: number;
  /** Destination signposting text Mapbox returns for highway exits ("toward
   *  Hudson", "Tarrytown / White Plains"). Surfaced in the complex-direction
   *  overlay so the driver can match the maneuver to the road sign. */
  destinations?: string;
}

export interface RawRoute {
  /** DISPLAY/scoring geometry. On very long trips this is downsampled (see
   *  capRouteGeometry in generateRoutes) so scoring + map render stay fast;
   *  the full-resolution line lives on `navCoordinates`. On normal trips this
   *  is the full geometry and `navCoordinates` is undefined. */
  coordinates: LngLat[];
  /** Full-resolution geometry for turn-by-turn navigation (meter-level
   *  precision near maneuvers). Only set when `coordinates` was downsampled. */
  navCoordinates?: LngLat[];
  distance: number; // meters
  duration: number; // seconds
  steps: RouteStep[];
  /** Meters of the route classified as motorway/highway by Mapbox. */
  motorwayMeters?: number;
  /** Meters on motorway + trunk + primary roads (the "high-traffic
   *  arterial" bucket). For Pleasant-mode metrics we treat these together
   *  because routes on US-9, NY-100, NY-22 etc. are exactly as un-pleasant
   *  as routes on interstates. */
  highTrafficMeters?: number;
  /** Meters of the route classified as a tunnel by Mapbox. */
  tunnelMeters?: number;
  /** Meters of the route classified as toll by Mapbox. */
  tollMeters?: number;
  /** Meters of the route that ran on a known legendary scenic highway
   *  (e.g. Pacific Coast Highway, Route 66, Blue Ridge Parkway). */
  epicHighwayMeters?: number;
  /** Display labels for any epic highways the route uses. */
  epicHighwayLabels?: string[];
  /** Meters by Mapbox street class (primary/secondary/tertiary/etc.). */
  roadClassMeters?: Record<string, number>;
  /** Step indices that mark arrival at a user-added intermediate stop
   *  (one entry per leg except the final one). Lets the UI highlight
   *  "you'll stop here" rows in the directions list. */
  stopStepIndices?: number[];
  /** Per-segment live congestion ("low"|"moderate"|"heavy"|"severe"|"unknown"),
   *  one entry per pair of consecutive `coordinates`. Drives the traffic-colored
   *  route line. Undefined on profiles that don't return it. */
  congestion?: string[];
  /** Named scenic features (parks, water, historic places) the route passes,
   *  WITH coordinates. Populated during route generation from OSM data.
   *  Used so scenic narration can fire on the route's own features — a
   *  river, a park — not only on Google POIs. */
  scenicPoints?: ScenicPoint[];
  /** Charming towns this route was deliberately threaded through (ordered
   *  start→end). Set on "towns-corridor" road-trip routes so the card can read
   *  "through Charleston and Savannah". */
  townNames?: string[];
  /** Curated pretty streets this variety walk was threaded through (ordered
   *  start→end). Set on the "nice city streets" walk so the card can read
   *  "through Jane St, Perry St & Bleecker St". */
  varietyStreets?: string[];
  /** Major park this walk was routed THROUGH (e.g. "Washington Square Park").
   *  Set on the through-the-park walk so the card can read "Through Washington
   *  Square Park". */
  parkName?: string;
}

/** A named scenic feature on a route, with a coordinate to detect approach. */
export interface ScenicPoint {
  /** Narration taxonomy slug — "park" | "water" | "historic" | "nature". */
  kind: "park" | "water" | "historic" | "nature";
  name: string;
  lng: number;
  lat: number;
}

const MODE_MAP: Record<TravelMode, string> = {
  // Use driving-traffic so route choice and ETAs reflect live congestion.
  driving: "driving-traffic",
  walking: "walking",
  biking: "cycling",
};

/** Mapbox `exclude=` values supported on the driving profile. */
export type RouteExclude = "motorway" | "toll" | "ferry";

/** Default seconds of extra drive time we'll tolerate to keep a route off
 *  the motorway. Above this we fall back to allowing the highway. Callers
 *  can override per-request (e.g. "scenic" tolerance passes Infinity to
 *  always avoid highways no matter the cost). */
const DEFAULT_NO_HIGHWAY_OVERRIDE_SECONDS = 20 * 60; // 20 min

function namedHighTrafficArterial(name?: string, ref?: string, instruction?: string): string | undefined {
  const parts = [ref, name, instruction].filter(Boolean).map((s) => String(s));
  const text = parts.join(" ");
  const normalized = text.replace(/[‐‑‒–—]/g, "-");

  // Provider class metadata is inconsistent. Infer arterial roads from the
  // visible route shields/names too, not just Mapbox `primary` classes:
  // US-9, U.S. Route 9, NY-100, State Route 22, SR 7, CA-1, NJ 17, etc.
  // ONLY treat reliably-major designations as high-traffic arterials. A bare
  // state/provincial shield (NY-120, NJ-17, CT-15) or generic "Route N" is NOT
  // a busy-road signal — those numbers cover thousands of quiet 2-lane country
  // roads (King Street/NY 120, Bedford Road/NY 117). Over-flagging them made a
  // pleasant back-roads route read as "Mostly on the highway" (Morgan, real
  // drive). Interstates, US highways, and parkways ARE reliably major; genuinely
  // busy state routes are still caught by their motorway/trunk road class.
  // HIGHWAY TIERING (Morgan: "not all highways are the same — depends on lanes").
  // Only genuinely BIG highways count as "highway" for the calm signal: limited-
  // access interstates and motorway/trunk-class roads (handled by road class
  // elsewhere) plus divided 4-lane parkways. A US-route shield is NOT a reliable
  // size signal — US-9 / Albany Post Road through the Hudson Valley are pleasant
  // 2-lane river roads, yet the old US-\d+ rule force-counted them as 97% highway,
  // which is exactly why the calmest route could never win the "calm" test. So we
  // no longer promote bare US-routes (or Albany Post Road) by NAME. A genuinely
  // big US route is still caught by its motorway/trunk road class; a wide primary
  // is caught by the lane-count proxy in the step loop (see fetchRoutes).
  const interstate = normalized.match(/\b(?:I|Interstate)\s*-?\s*(\d{1,3})\b/i);
  if (interstate) return `I-${interstate[1]}`;

  // Saw Mill River Road is the Saw Mill River Parkway corridor — a real divided
  // arterial. (Albany Post Road removed — it's a curated PLEASANT road.)
  if (/\bSaw Mill River Road\b/i.test(normalized)) return "named arterial";
  // Parkways (#116). The Taconic, Saw Mill, Hutch, Merritt, Bronx River,
  // Sprain, Henry Hudson, etc. are 4-lane divided 55mph roads with a
  // "scenic" name. OSM tags them inconsistently (motorway / trunk /
  // primary). Always treat as named arterial — Pleasant should avoid
  // them. The epic-highway list still exempts Blue Ridge Parkway,
  // Skyline Drive, etc. via a separate code path.
  if (/\bParkway\b/i.test(normalized)) return "Parkway";
  return undefined;
}

/** Fetch up to 3 alternative routes from Mapbox Directions API. Returns real coordinates.
 *  Optional `waypoints` are inserted in order between start and end (max ~23 with Mapbox).
 *  Optional `excludes` translates to Mapbox `exclude=` (driving profile only).
 *
 *  GLOBAL RULE — driving routes always avoid motorways by default. We issue
 *  a parallel highway-allowed request as a fallback; if the no-highway route
 *  costs more extra time than `noHighwayOverrideSeconds`, we use the highway
 *  one instead. Pass `Infinity` to always prefer the no-highway route
 *  regardless of detour cost (used for the "scenic" tolerance setting).
 *  The user's explicit `excludes` override this (e.g. they're already opted
 *  into "no-highways" — we honor it strictly with no fallback). */
export async function fetchRoutes(
  start: LngLat,
  end: LngLat,
  mode: TravelMode,
  waypoints: LngLat[] = [],
  excludes: RouteExclude[] = [],
  noHighwayOverrideSeconds: number = DEFAULT_NO_HIGHWAY_OVERRIDE_SECONDS,
  /** Loop fix: waypoints shape the path without creating legs/stops. */
  silentVias = false,
): Promise<RawRoute[]> {
  if (!MAPBOX_TOKEN) throw new Error("VITE_MAPBOX_TOKEN missing");

  // ⚠️ `noHighwayOverrideSeconds` IS NOT READ ANYWHERE IN THIS FUNCTION.
  //
  // It is declared in the signature and referenced in the doc comment above,
  // and that is all. Callers passing 0 to mean "no penalty budget — give me
  // the genuinely fastest route" have been getting the DEFAULT behaviour
  // instead: both pools merged, motorway-avoiding routes FIRST (see the return
  // at the end of this branch).
  //
  // That silently broke mid-drive "Take fastest", which took rs[0] and so got
  // a no-highway route — 44 km/h on an autostrada (Morgan, Aug 11). Fixed at
  // the call site by sorting on duration, which is correct regardless.
  //
  // Left in place rather than removed because deleting a parameter changes
  // every call site, and that is a bigger change than this hotfix should make.
  // If you want "fastest", either sort the result by duration or call
  // fetchRoutesRaw directly with no motorway exclude — do NOT trust this
  // argument to do it for you.
  void noHighwayOverrideSeconds;

  // Driving default: avoid motorways. If caller already passed motorway,
  // they get strict no-highway with no fallback.
  const callerForcedNoHwy = excludes.includes("motorway");
  const wantNoHwyDefault = mode === "driving" && !callerForcedNoHwy;

  if (wantNoHwyDefault) {
    // Fire both in parallel: the preferred no-highway route and a highway-
    // allowed fallback. Pick whichever satisfies the smart-override rule.
    const [noHwy, withHwy] = await Promise.all([
      fetchRoutesRaw(start, end, mode, waypoints, [...excludes, "motorway"], silentVias).catch(() => []),
      fetchRoutesRaw(start, end, mode, waypoints, excludes, silentVias).catch(() => []),
    ]);
    if (noHwy.length === 0) return withHwy;
    if (withHwy.length === 0) return noHwy;
    // EPIC HIGHWAY EXCEPTION: if the highway-allowed result uses a known
    // legendary scenic highway (PCH, Route 66, Blue Ridge Pkwy, etc.) for
    // a meaningful stretch, return BOTH pools merged so scoring can pick
    // the epic one as the most pleasant route — beating the backroad.
    const epicWithHwy = withHwy.filter(
      (r) => (r.epicHighwayMeters ?? 0) > 5000 ||
        ((r.epicHighwayMeters ?? 0) > 0 && (r.epicHighwayMeters ?? 0) / Math.max(1, r.distance) > 0.15),
    );
    if (epicWithHwy.length > 0) {
      // De-dupe by reference identity; let scoring choose the winner.
      return [...epicWithHwy, ...noHwy];
    }
    const bestNoHwy = Math.min(...noHwy.map((r) => r.duration));
    const bestWithHwy = Math.min(...withHwy.map((r) => r.duration));
    // Merge both pools so the classifier has enough diversity to surface
    // multiple cards (Direct vs Wander vs Adventure). The pleasant/scenic
    // gates downstream already filter out arterial-heavy routes when
    // appropriate, so handing them more candidates only helps.
    // SCENIC MAY BE AS LONG AS IT NEEDS TO BE (Morgan). The old ceiling
    // discarded the entire no-highway pool whenever avoiding highways
    // cost "too much" — which is how Rhinebeck came back with a single
    // parkway card. There is no such thing as too long for a route the
    // user is choosing on purpose: BOTH pools always go to scoring, and
    // the existing 2×-fastest sanity cap downstream is the only limit.
    return [...noHwy, ...withHwy];
  }

  // Walking/biking: avoid ferries by default — pedestrians don't expect a
  // ferry leg in the middle of a city walk. Fall back to allowing ferries
  // only if the no-ferry request returns nothing (true island crossing).
  if (mode === "walking" || mode === "biking") {
    const noFerry = await fetchRoutesRaw(
      start, end, mode, waypoints,
      Array.from(new Set([...excludes, "ferry"])),
      silentVias,
    ).catch(() => [] as RawRoute[]);
    if (noFerry.length > 0) return noFerry;
    return fetchRoutesRaw(start, end, mode, waypoints, excludes, silentVias);
  }

  return fetchRoutesRaw(start, end, mode, waypoints, excludes);
}

/** Minimum-duration pick — NEVER array position (release spec item 3). */
export function pickFastestRoute(routes: RawRoute[]): RawRoute | null {
  if (routes.length === 0) return null;
  return routes.reduce((a, b) => (b.duration < a.duration ? b : a));
}

/** THE ONE TRUE "FASTEST" (release spec item 3, Aug 11).
 *
 *  `fetchRoutes` merges `[...noHwy, ...withHwy]`, so `rs[0]` is a
 *  MOTORWAY-AVOIDING route — and `noHighwayOverrideSeconds` is declared but
 *  never read, so passing 0 changes nothing. Every caller that trusted
 *  array position for "fastest" got the scenic option (44 km/h on an
 *  autostrada, Aug 11). This is the explicit contract instead:
 *
 *    • same Mapbox profile map as every other request (driving →
 *      driving-traffic, so the duration is already the traffic answer)
 *    • motorways ALLOWED — a caller-supplied "motorway" exclude is
 *      stripped, because this function's one job is the unhandicapped
 *      direct route
 *    • no Pleasant/Scenic preference of any kind
 *    • other user excludes (toll / ferry) pass through untouched
 *    • returns the MINIMUM-DURATION candidate, tagged "mapbox-direct"
 *
 *  Use this for: initial Direct, Take Fastest, off-route reroute while
 *  wantsFastest, add/remove-stop while wantsFastest. Do NOT reach for
 *  fetchRoutes(...)[0] to mean "fastest" — that is the bug this exists
 *  to end. */
export async function fetchDirectRoute(
  start: LngLat,
  end: LngLat,
  mode: TravelMode,
  waypoints: LngLat[] = [],
  excludes: RouteExclude[] = [],
): Promise<RawRoute | null> {
  const rs = await fetchRoutesRaw(
    start,
    end,
    mode,
    waypoints,
    excludes.filter((e) => e !== "motorway"),
    false,
  );
  const winner = pickFastestRoute(rs);
  if (winner) tagRouteSource(winner, "mapbox-direct");
  // NO SILENT VERDICTS (rule 17): confess the pool and the pick.
  // eslint-disable-next-line no-console
  console.log(
    `[direct] pool=${rs.length} durations=${rs
      .map((r) => Math.round(r.duration / 60))
      .join("/")}min → ${
      winner
        ? `${Math.round(winner.duration / 60)}min (minimum-duration, highways allowed)`
        : "NONE"
    }`,
  );
  return winner;
}

/** (#128) Three-pool candidate generator.
 *
 *  The Sleepy Hollow → Tarrytown convergence bug exposed a structural
 *  problem: Pleasant and Scenic were re-ranking the SAME pool of Mapbox
 *  alternatives, so on short trips they always picked the same route.
 *
 *  This function gets *different* candidates from the engine for each
 *  category by sending three distinct requests in parallel:
 *
 *    DIRECT  — engine fastest, highways allowed. The literal Mapbox
 *              best for time.
 *    PLEASANT — engine fastest with `exclude=motorway`. Same trip but
 *              the engine is forbidden from using freeways. Gives us
 *              backroad alternatives without anchoring anywhere.
 *    SCENIC  — engine routes through user-provided anchor waypoints
 *              (parks, water, scenic byways), `exclude=motorway`. The
 *              anchors force the geometry to detour through scenic
 *              features; if no anchors are available for this corridor,
 *              the scenic pool is intentionally EMPTY and the caller
 *              should surface a "no scenic option for this trip" card.
 *
 *  All three queries run in parallel — total wall time ≈ one Mapbox
 *  request. Mapbox usage cost goes up ~3× per search; acceptable for
 *  the UX win, watch the dashboard.
 *
 *  Walking/biking: motorway exclude is a driving-only Mapbox concept,
 *  so PLEASANT collapses to "engine fastest with ferries excluded" and
 *  SCENIC still anchors. Direct and Pleasant may return the same
 *  geometry for walking — the caller handles "duplicate pools" via
 *  the empty-category UX. */
export async function fetchThreeCategoryPools(
  start: LngLat,
  end: LngLat,
  mode: TravelMode,
  /** Anchor waypoints for the Scenic pool — caller fetches these via
   *  `findPleasantWaypoints` or equivalent. Pass an empty array if no
   *  scenic anchors exist within the corridor; the scenic pool will be
   *  empty and the UI should show "no scenic option" gracefully. */
  scenicAnchors: LngLat[] = [],
): Promise<{
  direct: RawRoute[];
  pleasant: RawRoute[];
  scenic: RawRoute[];
}> {
  if (!MAPBOX_TOKEN) throw new Error("VITE_MAPBOX_TOKEN missing");

  // Mapbox accepts up to ~23 waypoints between start and end, but for
  // scenic anchoring 1-2 is the sweet spot — more start to force-march
  // the geometry through every dot and produce noticeably worse trips.
  // Cap defensively.
  const capped = scenicAnchors.slice(0, 2);

  const driving = mode === "driving";

  const [direct, pleasant, scenic] = await Promise.all([
    // Direct: alternatives=true, no excludes. Highways allowed.
    fetchRoutesRaw(start, end, mode, [], []).catch(() => [] as RawRoute[]),
    // Pleasant: exclude=motorway (driving-only). Walking/biking can't
    // exclude motorway — they get the same flat request without anchors.
    fetchRoutesRaw(start, end, mode, [], driving ? ["motorway"] : []).catch(
      () => [] as RawRoute[],
    ),
    // Scenic: anchored on scenic waypoints. Only fetched when anchors
    // exist; otherwise the scenic pool is intentionally empty.
    capped.length > 0
      ? fetchRoutesRaw(start, end, mode, capped, driving ? ["motorway"] : []).catch(
          () => [] as RawRoute[],
        )
      : Promise.resolve([] as RawRoute[]),
  ]);

  return { direct, pleasant, scenic };
}

/** Single Mapbox Directions request — used by the smart wrapper above. */
async function fetchRoutesRaw(
  start: LngLat,
  end: LngLat,
  mode: TravelMode,
  waypoints: LngLat[],
  excludes: RouteExclude[],
  /** SILENT VIAS (the Loop fake-stops fix): when true, intermediate
   *  coordinates shape the path but create NO legs — no "You have
   *  arrived", no STOP rows, one continuous set of directions. */
  silentVias = false,
): Promise<RawRoute[]> {
  const profile = `mapbox/${MODE_MAP[mode]}`;
  const allPts = [start, ...waypoints, end];
  const coords = allPts.map((p) => `${p[0]},${p[1]}`).join(";");
  // Alternatives are only allowed without intermediate waypoints.
  const alt = waypoints.length === 0 ? "true" : "false";
  const silent = silentVias && waypoints.length > 0
    ? `&waypoints=0;${allPts.length - 1}`
    : "";
  // `exclude=` works on driving, walking, and cycling profiles. Mapbox
  // accepts `ferry` for all three; `motorway`/`toll` are driving-only.
  const dedupedExcludes = Array.from(new Set(excludes)).filter((e) =>
    mode === "driving" ? true : e === "ferry",
  );
  const exclude = dedupedExcludes.length > 0
    ? `&exclude=${dedupedExcludes.join(",")}` : "";
  // Per-segment congestion (clear/moderate/heavy/severe) for the traffic-colored
  // route line. Only the driving-traffic profile returns it.
  const annotations = profile.includes("driving-traffic") ? "&annotations=congestion" : "";
  const url =
    `https://api.mapbox.com/directions/v5/${profile}/${coords}` +
    `?alternatives=${alt}&geometries=polyline6&overview=full&steps=true&language=en` +
    annotations +
    exclude +
    silent +
    `&access_token=${MAPBOX_TOKEN}`;

  // WEAK-SIGNAL CEILING (Morgan: "starting from a place with bad service"):
  // on one bar this fetch used to dangle for the OS default (60s+), and
  // several of them compound into minutes of silent nothing. 20s is enough
  // for any healthy network; past that we fail fast so the UI can say so
  // and retries/fallbacks take over.
  const ctl = new AbortController();
  const ceiling = setTimeout(() => ctl.abort(), 20_000);
  let res: Response;
  try {
    res = await fetch(url, { signal: ctl.signal });
  } finally {
    clearTimeout(ceiling);
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Routing failed (${res.status}): ${text}`);
  }
  const data = await res.json();
  if (!data.routes?.length) throw new Error("No routes found");

  // [raw] PROVIDER TRUTH (docs/OPEN_ETA_BUG.md, the one-line next step):
  // duration/distance exactly as Mapbox returned them, before anything in
  // this codebase touches them. One line per route settles whether the
  // ~44 km/h corridor pricing came back from the provider or was
  // manufactured downstream. Logging only — no behaviour change.
  for (let i = 0; i < data.routes.length; i++) {
    const rr = data.routes[i];
    const kmh = rr.duration > 0 ? (rr.distance / 1000) / (rr.duration / 3600) : 0;
    // eslint-disable-next-line no-console
    console.log(
      `[raw] mapbox ${profile} #${i} dur=${Math.round(rr.duration)}s` +
        (rr.duration_typical != null ? ` durTypical=${Math.round(rr.duration_typical)}s` : "") +
        ` dist=${Math.round(rr.distance)}m → ${kmh.toFixed(1)} km/h`,
    );
  }

  return data.routes.map((r: any): RawRoute => {
    const coordinates = decodePolyline(r.geometry, 6);
    const legs = (r.legs ?? []) as any[];
    // Per-segment congestion aligned to `coordinates` (one entry per pair of
    // consecutive points). Legs join at shared coords, so concatenating each
    // leg's annotation array yields the full route's congestion. Empty when the
    // profile didn't return it (walking/cycling) — the map then draws a plain line.
    const congestion = legs.flatMap(
      (leg: any) => (leg.annotation?.congestion ?? []) as string[],
    );
    // Track the cumulative step count so we know which step index marks the
    // *end of each leg* — i.e. the moment the user arrives at one of their
    // intermediate stops. The final leg ends at the destination, so we
    // skip it.
    const stopStepIndices: number[] = [];
    let stepCursor = 0;
    // Per-step road name + ref captured here so we can detect "epic" highways
    // (PCH, Route 66, Blue Ridge Parkway, etc.) further down.
    const roadSegments: { name?: string; ref?: string; meters: number }[] = [];
    const steps: RouteStep[] = legs.flatMap((leg: any, legIdx: number) => {
      const legSteps = (leg.steps ?? []).map((s: any) => {
        const classSet = new Set<string>();
        for (const inter of (s.intersections ?? [])) {
          const streetClass = inter.mapbox_streets_v8?.class;
          if (typeof streetClass === "string") classSet.add(streetClass);
          for (const c of (inter.classes ?? [])) classSet.add(c);
        }
        const instruction = s.maneuver?.instruction ?? "Continue";
        const roadName = s.name as string | undefined;
        const roadRef = s.ref as string | undefined;
        const highTrafficName = namedHighTrafficArterial(roadName, roadRef, instruction);
        roadSegments.push({ name: roadName, ref: roadRef, meters: s.distance ?? 0 });
        // Lane guidance lives on `intersections[].lanes`. The relevant
        // intersection for navigation is the LAST one in the step — that's
        // the intersection at which the upcoming maneuver happens. Each
        // lane: { valid: bool, indications: ["left"|"right"|"straight"|...] }.
        const lastInter = (s.intersections ?? [])[((s.intersections ?? []).length || 1) - 1];
        const rawLanes = (lastInter?.lanes ?? []) as Array<{ valid?: boolean; indications?: string[] }>;
        const lanes = rawLanes.length > 0
          ? rawLanes.map((l) => ({
              valid: Boolean(l.valid),
              indications: Array.isArray(l.indications) ? l.indications.filter((x) => typeof x === "string") : [],
            }))
          : undefined;
        // (Morgan: tier highways by class + LANES.) A primary-class road that's
        // genuinely WIDE behaves like a big highway, so count it as one even
        // though its class is only "primary". The threshold is deliberately high
        // (5+): Mapbox reports TURN-lane guidance, and turn pockets inflate the
        // count at junctions, so pleasant 2-lane roads routinely show 3–4 lanes
        // at an intersection. Requiring 5+ keeps US-9 / Albany Post Road (the
        // pleasant Hudson roads) OUT while still catching true 6-lane US arterials.
        const isWidePrimary =
          classSet.has("primary") && !highTrafficName && (lanes?.length ?? 0) >= 5;
        const effectiveHighTrafficName = highTrafficName ?? (isWidePrimary ? "wide arterial" : undefined);
        return {
          instruction,
          distance: s.distance ?? 0,
          duration: s.duration ?? 0,
          roadName,
          roadRef,
          highTrafficName: effectiveHighTrafficName,
          classes: Array.from(classSet),
          lanes,
          maneuverType: typeof s.maneuver?.type === "string" ? s.maneuver.type : undefined,
          maneuverModifier: typeof s.maneuver?.modifier === "string" ? s.maneuver.modifier : undefined,
          exit: typeof s.maneuver?.exit === "number" ? s.maneuver.exit : undefined,
          destinations: typeof s.destinations === "string" && s.destinations.trim().length > 0
            ? s.destinations
            : undefined,
        } as RouteStep;
      });
      stepCursor += legSteps.length;
      // Mark the last step of each non-final leg as a "stop arrival" step.
      if (legIdx < legs.length - 1 && legSteps.length > 0) {
        stopStepIndices.push(stepCursor - 1);
      }
      return legSteps;
    });
    let motorwayMeters = 0, tunnelMeters = 0, tollMeters = 0, highTrafficMeters = 0;
    const roadClassMeters: Record<string, number> = {};
    for (const s of steps) {
      const classes = s.classes ?? [];
      if (classes.includes("motorway")) motorwayMeters += s.distance;
      // High-traffic arterials: motorway, trunk, primary count fully.
      // Secondary state-route arterials (US-1, NY-22, NY-119...) are tagged
      // `secondary` in OSM but feel just as bad to drive on, so we count
      // them at HALF weight unless the name regex already flagged them
      // (in which case they count fully via highTrafficName below).
      // Tier by size (Morgan). Motorway/trunk (and name/lane-flagged big roads —
      // parkways, wide arterials) are the genuine "highway" tier and count fully.
      // Plain primary (US-9 etc.) and secondary state routes feel like normal
      // main roads, not interstates, so they count at HALF — this is what keeps a
      // pleasant 2-lane US route from reading as a highway slog.
      const isBigHighway =
        classes.includes("motorway") || classes.includes("trunk");
      if (isBigHighway || !!s.highTrafficName) {
        highTrafficMeters += s.distance;
      } else if (classes.includes("primary") || classes.includes("secondary")) {
        highTrafficMeters += s.distance * 0.5;
      }
      if (classes.includes("tunnel")) tunnelMeters += s.distance;
      if (classes.includes("toll")) tollMeters += s.distance;
      for (const c of classes) {
        if (c === "tunnel" || c === "toll" || c === "restricted" || c === "ferry") continue;
        roadClassMeters[c] = (roadClassMeters[c] ?? 0) + s.distance;
      }
    }
    const { meters: epicHighwayMeters, labels: epicHighwayLabels } =
      detectEpicHighways(roadSegments, coordinates);
    return {
      coordinates,
      distance: r.distance ?? totalDistance(coordinates),
      duration: r.duration ?? 0,
      steps,
      motorwayMeters,
      highTrafficMeters,
      tunnelMeters,
      tollMeters,
      roadClassMeters,
      epicHighwayMeters,
      epicHighwayLabels,
      stopStepIndices,
      congestion: congestion.length > 0 ? congestion : undefined,
    };
  });
}

/* ─── Google Places autocomplete: cost-aware client ─────────────────── *
 * Aggressive optimizations to stay within free credit:
 *   1. In-memory result cache (5 min) keyed by query+proximity.
 *   2. In-flight dedup so multiple components asking for the same query
 *      share a single network round-trip.
 *   3. Session tokens — every keystroke in an autocomplete session shares
 *      one token so the eventual Place Details lookup is billed inside
 *      the session bundle (single Autocomplete charge).
 *   4. Lightweight per-session usage counters on `window.__placesUsage`.
 *   5. Graceful fallback to Mapbox when Places fails / is rate-limited.
 * ──────────────────────────────────────────────────────────────────── */

const AC_CACHE_TTL_MS = 5 * 60_000;
const AC_CACHE_MAX = 200;
const acCache = new Map<string, { ts: number; suggestions: GeoSuggestion[] }>();
const acInflight = new Map<string, Promise<GeoSuggestion[]>>();
const detailsCache = new Map<string, { ts: number; center: LngLat }>();

const cacheKey = (q: string, p?: LngLat) =>
  `${q.trim().toLowerCase()}|${p ? `${p[0].toFixed(3)},${p[1].toFixed(3)}` : "-"}`;

const trimCache = <T,>(m: Map<string, T>, max: number) => {
  if (m.size <= max) return;
  const drop = m.size - max;
  let i = 0;
  for (const k of m.keys()) { if (i++ >= drop) break; m.delete(k); }
};

interface PlacesUsage {
  autocompleteCalls: number;
  autocompleteCacheHits: number;
  resolveCalls: number;
  resolveCacheHits: number;
  fallbackCalls: number;
  startedAt: string;
  lastSummary?: string;
}

function getUsage(): PlacesUsage {
  if (typeof window === "undefined") {
    return { autocompleteCalls: 0, autocompleteCacheHits: 0, resolveCalls: 0, resolveCacheHits: 0, fallbackCalls: 0, startedAt: new Date().toISOString() };
  }
  const w = window as unknown as { __placesUsage?: PlacesUsage };
  if (!w.__placesUsage) {
    w.__placesUsage = {
      autocompleteCalls: 0, autocompleteCacheHits: 0,
      resolveCalls: 0, resolveCacheHits: 0, fallbackCalls: 0,
      startedAt: new Date().toISOString(),
    };
  }
  return w.__placesUsage;
}

function logUsageIfChanged() {
  if (typeof window === "undefined") return;
  const u = getUsage();
  const summary = `ac=${u.autocompleteCalls}/${u.autocompleteCalls + u.autocompleteCacheHits} resolve=${u.resolveCalls}/${u.resolveCalls + u.resolveCacheHits} fb=${u.fallbackCalls}`;
  if (summary !== u.lastSummary) {
    u.lastSummary = summary;
     
    console.debug(`[places] usage ${summary} (live → cached/network ratios)`);
  }
}

// Session token shared across autocomplete keystrokes. `endAutocompleteSession`
// is called when the user picks a suggestion (or when an input loses focus
// for >30s) so the next session starts fresh.
let acSessionToken: string | null = null;
let acSessionStartedAt = 0;
const SESSION_MAX_MS = 3 * 60_000; // hard cap — sessions Google honors
function currentSessionToken(): string {
  const now = Date.now();
  if (!acSessionToken || now - acSessionStartedAt > SESSION_MAX_MS) {
    // Stable per-session token. crypto.randomUUID is fine — Google requires
    // an opaque, unique-per-session identifier (any UUID works).
    acSessionToken = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    acSessionStartedAt = now;
  }
  return acSessionToken;
}

/** Call after the user explicitly selects a suggestion so the next
 *  autocomplete keystroke starts a fresh billing session. Idempotent. */
export function endAutocompleteSession(): void {
  acSessionToken = null;
  acSessionStartedAt = 0;
}

/** Mapbox geocoding autocomplete — works with the same Mapbox token. */
export interface GeoSuggestion {
  id: string;
  name: string;
  place: string;
  /** [lng, lat]. May be [0, 0] when Places returned a prediction without
   *  resolved coords — in that case `resolveSuggestionCoords(s)` performs
   *  the on-demand Place Details lookup (only at user selection time). */
  center: LngLat;
  /** Category (text-search) results only: "Open · 4.6★ (1.2k)" material.
   *  Address predictions never carry these. */
  rating?: number;
  userRatingCount?: number;
  openNow?: boolean;
}

/** "4.6★ (1.2k) · Open" — compact detail line shared by phone rows and
 *  car rows. Empty string when the suggestion has no place metadata. */
export function suggestionDetail(s: GeoSuggestion): string {
  const parts: string[] = [];
  if (typeof s.rating === "number" && s.rating > 0) {
    const count = typeof s.userRatingCount === "number" && s.userRatingCount > 0
      ? s.userRatingCount >= 1000
        ? ` (${(s.userRatingCount / 1000).toFixed(1)}k)`
        : ` (${s.userRatingCount})`
      : "";
    parts.push(`${s.rating.toFixed(1)}★${count}`);
  }
  if (s.openNow === true) parts.push("Open");
  else if (s.openNow === false) parts.push("Closed");
  return parts.join(" · ");
}

/** Returns true when a suggestion still needs a coords resolve. */
export function needsResolve(s: GeoSuggestion): boolean {
  return !s.center || (s.center[0] === 0 && s.center[1] === 0);
}

/** Mapbox Geocode v6 forward search — addresses, TOWNS, neighborhoods,
 *  regions, POIs. Used as the fallback when Google fails entirely AND
 *  alongside Google for town-shaped queries (the T+L "Windham, NY didn't
 *  appear" bug: Google's ranking can fill every slot with businesses and
 *  leave out the town itself; Mapbox is excellent at localities). */
async function mapboxForward(
  q: string,
  proximity?: LngLat,
): Promise<Array<GeoSuggestion & { featureType?: string }>> {
  if (!MAPBOX_TOKEN) return [];
  getUsage().fallbackCalls++;
  const params = new URLSearchParams({
    q,
    access_token: MAPBOX_TOKEN,
    autocomplete: "true",
    limit: "5",
    // International: no country filter — works worldwide. Proximity bias
    // (set below when we have the user's location) keeps local results first.
    types: "address,street,place,locality,neighborhood,district,region,postcode,poi",
  });
  if (proximity) params.set("proximity", `${proximity[0]},${proximity[1]}`);
  const url = `https://api.mapbox.com/search/geocode/v6/forward?${params}`;
  // Weak-signal ceiling — this geocoder must fail fast too.
  const gCtl = new AbortController();
  const gCeiling = setTimeout(() => gCtl.abort(), 8_000);
  let res: Response;
  try {
    res = await fetch(url, { signal: gCtl.signal });
  } catch {
    return [];
  } finally {
    clearTimeout(gCeiling);
  }
  if (!res.ok) return [];
  const data = await res.json();
  return (Array.isArray(data?.features) ? data.features : [])
    .map((f: any): (GeoSuggestion & { featureType?: string }) | null => {
      const p = f?.properties ?? {};
      const coords =
        (Array.isArray(p?.coordinates) && p.coordinates.length === 2 && [p.coordinates[0], p.coordinates[1]]) ||
        (Array.isArray(f?.geometry?.coordinates) && f.geometry.coordinates.length === 2 && f.geometry.coordinates) ||
        (typeof p?.coordinates?.longitude === "number" && typeof p?.coordinates?.latitude === "number" &&
          [p.coordinates.longitude, p.coordinates.latitude]);
      const name = p?.name ?? p?.name_preferred ?? "";
      if (!coords || !name) return null;
      return {
        id: p?.mapbox_id ?? `${coords[0]},${coords[1]}`,
        name,
        place: p?.full_address ?? p?.place_formatted ?? name,
        center: [coords[0], coords[1]] as LngLat,
        featureType: typeof p?.feature_type === "string" ? p.feature_type : undefined,
      };
    })
    .filter((s: (GeoSuggestion & { featureType?: string }) | null): s is GeoSuggestion & { featureType?: string } => s !== null);
}

/** Reverse-geocode a point to a short street address — used to label a
 *  GPS-origin trip with something readable ("423 S 900 E") instead of the
 *  literal placeholder "Current location" (PLE-39: that placeholder was
 *  leaking into the "Name this trip" default text). Best-effort: returns
 *  null on any failure/timeout so callers can fall back to the placeholder. */
export async function reverseGeocode(coords: LngLat): Promise<string | null> {
  if (!MAPBOX_TOKEN) return null;
  const params = new URLSearchParams({
    longitude: String(coords[0]),
    latitude: String(coords[1]),
    access_token: MAPBOX_TOKEN,
    types: "address,street,place",
  });
  const url = `https://api.mapbox.com/search/geocode/v6/reverse?${params}`;
  const ctl = new AbortController();
  const ceiling = setTimeout(() => ctl.abort(), 3_000);
  let res: Response;
  try {
    res = await fetch(url, { signal: ctl.signal });
  } catch {
    return null;
  } finally {
    clearTimeout(ceiling);
  }
  if (!res.ok) return null;
  try {
    const data = await res.json();
    const p = data?.features?.[0]?.properties;
    return p?.full_address ?? p?.place_formatted ?? p?.name ?? null;
  } catch {
    return null;
  }
}

/** Resolve a Places prediction's coordinates on demand (single Place
 *  Details call). Cached by placeId for 5 min so duplicate selects /
 *  re-renders are free. Falls back to the suggestion's own (possibly
 *  zero) center when Places is unavailable. */
export async function resolveSuggestionCoords(s: GeoSuggestion): Promise<LngLat> {
  if (!needsResolve(s)) return s.center;
  const cached = detailsCache.get(s.id);
  if (cached && Date.now() - cached.ts < AC_CACHE_TTL_MS) {
    getUsage().resolveCacheHits++;
    logUsageIfChanged();
    return cached.center;
  }
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    getUsage().resolveCalls++;
    const { data, error } = await supabase.functions.invoke("places-autocomplete", {
      body: { action: "resolve", placeId: s.id, sessionToken: currentSessionToken() },
    });
    if (!error && Array.isArray(data?.center) && data.center.length === 2) {
      const center: LngLat = [data.center[0], data.center[1]];
      detailsCache.set(s.id, { ts: Date.now(), center });
      trimCache(detailsCache, AC_CACHE_MAX);
      logUsageIfChanged();
      return center;
    }
  } catch {
    // fall through
  }
  // MAPBOX RESCUE (Morgan, Aug 6, #66): when the Places resolve fails —
  // API disabled, cold function, rate limit — the old code returned
  // s.center, which for an unresolved prediction is [0, 0]: Null Island,
  // an ocean point off Africa. The router then truthfully reported "no
  // drivable route between those points." Geocode the suggestion's own
  // text through Mapbox instead; only a double failure returns the zero
  // center, and the search-submit guard now refuses to route to it.
  try {
    const rescued = await mapboxForward(
      s.place && s.place !== s.name ? `${s.name}, ${s.place}` : s.name,
    );
    const top = rescued[0];
    if (top && (top.center[0] !== 0 || top.center[1] !== 0)) {
      console.log(`[search] resolve rescued via Mapbox: "${s.name}"`);
      detailsCache.set(s.id, { ts: Date.now(), center: top.center });
      trimCache(detailsCache, AC_CACHE_MAX);
      logUsageIfChanged();
      return top.center;
    }
  } catch { /* fall through to the honest zero */ }
  console.log(`[search] resolve FAILED for "${s.name}" — returning unresolved center`);
  logUsageIfChanged();
  return s.center;
}

/** Autocomplete a free-text query into place suggestions.
 *
 *  Strategy: try Google Places (via our edge proxy) first — it knows real
 *  businesses, restaurants, gyms, etc. that Mapbox's POI database mostly
 *  doesn't. If Google fails or returns nothing, fall back to Mapbox so the
 *  app keeps working when the edge function is cold or rate-limited. */
export async function geocodeAutocomplete(
  query: string,
  proximity?: LngLat,
): Promise<GeoSuggestion[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  const key = cacheKey(q, proximity);

  // Cache hit — return instantly, no network.
  const cached = acCache.get(key);
  if (cached && Date.now() - cached.ts < AC_CACHE_TTL_MS) {
    getUsage().autocompleteCacheHits++;
    logUsageIfChanged();
    return cached.suggestions;
  }

  // Dedup: another caller already has a request in flight for this exact
  // query+proximity → reuse its promise.
  const inflight = acInflight.get(key);
  if (inflight) return inflight;

  const promise = (async (): Promise<GeoSuggestion[]> => {
    // 1) Google Places (better business coverage).
    try {
      const [{ supabase }, { isOffline, raceTimeout }] = await Promise.all([
        import("@/integrations/supabase/client"),
        import("@/lib/net"),
      ]);
      // Offline: fail INSTANTLY (Morgan: "takes way too much time and
      // there is no communication") — the caller's empty-result handling
      // is the communication. One-bar limbo gets a 10s ceiling below.
      if (isOffline()) return [];
      getUsage().autocompleteCalls++;
      const invoked = await raceTimeout(
        supabase.functions.invoke("places-autocomplete", {
          body: { query: q, proximity, sessionToken: currentSessionToken() },
        }),
        10_000,
      );
      if (!invoked) throw new Error("autocomplete timeout");
      const { data, error } = invoked;
      if (!error && data?.suggestions?.length) {
        const out = (data.suggestions as Array<{
          id: string; name: string; place: string; center: LngLat;
          rating?: number; userRatingCount?: number; openNow?: boolean;
        }>)
          .map((s) => ({
            id: s.id, name: s.name, place: s.place, center: s.center,
            ...(typeof s.rating === "number" ? { rating: s.rating } : {}),
            ...(typeof s.userRatingCount === "number" ? { userRatingCount: s.userRatingCount } : {}),
            ...(typeof s.openNow === "boolean" ? { openNow: s.openNow } : {}),
          }));
        // TOWN GUARANTEE (the T+L bug — "I tried Windham, NY and it wasn't
        // available"): for town-shaped queries ("Something, XX"), run the
        // locality-strong geocoder too and PIN real towns at the top, so
        // Google's business-heavy ranking can never bury the place itself.
        let final = out;
        if (/,\s*[A-Za-z]/.test(q)) {
          const mb = await mapboxForward(q, proximity).catch(() => []);
          const towns = mb.filter((m) =>
            ["place", "locality", "district", "region"].includes(m.featureType ?? ""),
          );
          const dupOf = (t: GeoSuggestion) =>
            out.some((o) => {
              if (o.name.toLowerCase() === t.name.toLowerCase()) return true;
              if (!o.center || (o.center[0] === 0 && o.center[1] === 0)) return false;
              return (
                Math.abs(o.center[0] - t.center[0]) < 0.03 &&
                Math.abs(o.center[1] - t.center[1]) < 0.03
              );
            });
          const pinned = towns
            .filter((t) => !dupOf(t))
            .slice(0, 2)
            .map(({ featureType: _ft, ...rest }) => rest);
          if (pinned.length > 0) final = [...pinned, ...out].slice(0, 8);
        }
        acCache.set(key, { ts: Date.now(), suggestions: final });
        trimCache(acCache, AC_CACHE_MAX);
        logUsageIfChanged();
        return final;
      }
    } catch {
      // fall through to Mapbox
    }

    // 2) Mapbox fallback — extracted below so the TOWN GUARANTEE can also
    //    use it alongside Google, not only after a total Google failure.
    const out = await mapboxForward(q, proximity);
    acCache.set(key, { ts: Date.now(), suggestions: out });
    trimCache(acCache, AC_CACHE_MAX);
    logUsageIfChanged();
    return out;
  })();

  acInflight.set(key, promise);
  try {
    return await promise;
  } finally {
    acInflight.delete(key);
  }
}
